<template>
    <component :is="rootTag" :class="rootClasses" :data-testid="rootDataTestId">
        <div
            class="border-b border-gray-300 flex justify-between items-start px-4 md:px-6 py-4"
            :data-testid="`${testIdPrefix}-header-section`"
            v-if="$slots.header || header"
        >
            <div class="overflow-hidden w-full">
                <slot name="header">
                    <h2
                        :id="headerId"
                        class="text-xl p-0 m-0 font-semibold text-gray-600"
                        :data-testid="`${testIdPrefix}-header-default`"
                    >
                        {{ header }}
                    </h2>
                </slot>
            </div>
            <button
                class="ml-0 md:my-auto md:ml-6"
                type="button"
                aria-label="Close"
                data-testid="close-button"
                @click="$emit('close')"
            >
                <icon class="w-6 h-6" name="regular/remove" />
            </button>
        </div>
        <div :class="contentClasses" :data-testid="`${testIdPrefix}-content-section`">
            <activix-spinner
                :size="24"
                class="my-10"
                data-testid="loading-spinner"
                v-if="loading"
            />
            <slot v-else />
        </div>
        <div
            :class="actionsClasses"
            :data-testid="`${testIdPrefix}-actions-section`"
            v-if="$slots.actions"
        >
            <slot name="actions" v-if="!loading" />
        </div>
    </component>
</template>

<script setup lang="ts">
    /**
     * Shared frame for modal and drawer content: optional header, close action,
     * loading body, and actions footer without owning dialog semantics.
     */
    import { useId } from 'vue';

    type ClassValue = string | string[] | Record<string, boolean>;
    type Emits = (e: 'close') => void;

    defineEmits<Emits>();

    withDefaults(
        defineProps<{
            testIdPrefix: string;
            header?: string;
            loading?: boolean;
            rootTag?: string;
            rootDataTestId?: string;
            rootClasses?: ClassValue;
            contentClasses?: ClassValue;
            actionsClasses?: ClassValue;
        }>(),
        {
            rootTag: 'div',
            rootDataTestId: undefined,
            rootClasses: '',
            contentClasses: '',
            actionsClasses: 'flex justify-end flex-wrap px-4 md:px-6 py-4',
        }
    );

    const headerId = useId();
</script>
